/**
 * HomePage — Dashboard for Finding Your Voice
 *
 * Shows hero section, character profiles, progress overview,
 * quick actions, and a daily reflection quote.
 */

import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  BookOpen,
  ClipboardList,
  Compass,
  Wrench,
  Library,
  Trophy,
  ArrowRight,
} from 'lucide-react';

import { useProgress } from '../context/ProgressContext';
import { useGamification } from '../context/GamificationContext';
import { useCourtyardChat } from '../context/CourtyardContext';
import { CHARACTERS } from '../data/characters';
import CharacterBioCard from '../components/characters/CharacterBioCard';
import CharacterAvatar from '../components/characters/CharacterAvatar';
import CourtyardScene from '../components/art/CourtyardScene';
import Meander from '../components/ornament/Meander';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' },
  }),
};

const QUICK_ACTIONS = [
  { label: 'Courtyard', to: '/courtyard', icon: Compass, colour: 'text-courtyard-gold-ink', bg: 'bg-courtyard-gold/10', border: 'border-courtyard-gold/20' },
  { label: 'Assessment', to: '/assessment', icon: ClipboardList, colour: 'text-courtyard-aegean-dark', bg: 'bg-xenia-bg/40', border: 'border-courtyard-aegean/20' },
  { label: 'Evenings', to: '/evenings', icon: BookOpen, colour: 'text-courtyard-olive-dark', bg: 'bg-philemon-bg/40', border: 'border-courtyard-olive/20' },
  { label: 'Toolkit', to: '/toolkit', icon: Wrench, colour: 'text-courtyard-clay', bg: 'bg-theron-bg/40', border: 'border-courtyard-terracotta/20' },
  { label: 'Resources', to: '/resources', icon: Library, colour: 'text-courtyard-clay', bg: 'bg-courtyard-clay/5', border: 'border-courtyard-clay/15' },
  { label: 'Achievements', to: '/achievements', icon: Trophy, colour: 'text-courtyard-gold-ink', bg: 'bg-courtyard-gold/10', border: 'border-courtyard-gold/20' },
];

const HomePage = () => {
  const { hasSeenSceneSetter, moduleProgress, getAchievementContext } = useProgress();
  const { xp, level, levelProgress, checkAchievements } = useGamification();

  // Dashboard sweep: catches achievements earned outside their event moment
  // (return-day badges, level badges, scene setter read, resources visited).
  useEffect(() => {
    const timeoutId = setTimeout(() => checkAchievements(getAchievementContext()), 800);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate module completion stats
  const moduleStats = useMemo(() => {
    const total = 8;
    const completed = Object.values(moduleProgress).filter(
      (m) => m.status === 'completed'
    ).length;
    const inProgress = Object.values(moduleProgress).filter(
      (m) => m.status === 'started'
    ).length;
    return { total, completed, inProgress };
  }, [moduleProgress]);

  // From the Courtyard — day-keyed tip rotation (getTipOfTheDay updates
  // context state, so it must run in an effect, not during render)
  const { getTipOfTheDay } = useCourtyardChat();
  const [dailyTip, setDailyTip] = useState(null);
  useEffect(() => {
    setDailyTip(getTipOfTheDay());
  }, [getTipOfTheDay]);

  return (
    <>
      <Helmet>
        <title>Your Courtyard | Finding Your Voice</title>
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-10">
        {/* Hero Section */}
        <motion.section
          className="text-center rounded-2xl overflow-hidden bg-gradient-to-br from-courtyard-parchment via-courtyard-gold/10 to-theron-bg/30 border border-courtyard-gold/20"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          {/* A banner here, cut from the middle of the painting on wider screens so
              the page below is not pushed off the first screen. The whole picture
              is on the scene-setter page. */}
          <CourtyardScene eager className="aspect-video sm:aspect-[5/2] object-cover" />
          <div className="px-6 py-10">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-courtyard-stone">
              Finding Your Voice
            </h1>
            <p className="mt-3 text-lg text-courtyard-stone/80">
              Eight evenings in a courtyard in ancient Athens, and a cushion kept for you
            </p>
          </div>
          <Meander height={12} className="text-courtyard-clay/50" />
        </motion.section>

        {/* Scene Setter CTA */}
        {!hasSeenSceneSetter && (
          <motion.section
            className="text-center"
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={1}
          >
            <Card className="p-8 bg-gradient-to-br from-courtyard-gold/10 to-courtyard-parchment border-courtyard-gold/30">
              <p className="text-courtyard-stone/80 mb-4 text-lg">
                Your journey begins in an ancient Athenian courtyard, where three
                remarkable guides are waiting to meet you.
              </p>
              <Link to="/courtyard">
                <Button variant="primary" size="lg">
                  Enter the Courtyard
                </Button>
              </Link>
            </Card>
          </motion.section>
        )}

        {/* Character Profile Cards */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={2}
        >
          <h2 className="font-display text-2xl font-bold text-courtyard-stone mb-4">
            Your Guides
          </h2>
          <div className="space-y-4">
            {CHARACTERS.map((character) => (
              <CharacterBioCard key={character.id} character={character} />
            ))}
          </div>
        </motion.section>

        {/* Progress Section */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={3}
        >
          <h2 className="font-display text-2xl font-bold text-courtyard-stone mb-4">
            Your Progress
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Module Completion */}
            <Card className="p-5 bg-philemon-bg/30 border-courtyard-olive/20">
              <p className="text-sm text-courtyard-olive-dark font-medium mb-1">Evenings</p>
              <p className="text-2xl font-bold text-courtyard-stone">
                {moduleStats.completed}
                <span className="text-base font-normal text-courtyard-stone/80">
                  {' '}/ {moduleStats.total} completed
                </span>
              </p>
              {moduleStats.inProgress > 0 && (
                <p className="text-sm text-courtyard-gold-ink mt-1">
                  {moduleStats.inProgress} in progress
                </p>
              )}
              <div className="mt-3 h-2 bg-courtyard-stone/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-courtyard-olive rounded-full transition-all duration-500"
                  style={{
                    width: `${(moduleStats.completed / moduleStats.total) * 100}%`,
                  }}
                />
              </div>
            </Card>

            {/* XP & Level */}
            <Card className="p-5 bg-courtyard-gold/5 border-courtyard-gold/20">
              <p className="text-sm text-courtyard-gold-ink font-medium mb-1">
                Level {level.number}: {level.name}
              </p>
              <p className="text-2xl font-bold text-courtyard-stone">
                {xp}
                <span className="text-base font-normal text-courtyard-stone/80"> XP</span>
              </p>
              <div className="mt-3 h-2 bg-courtyard-stone/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-courtyard-gold rounded-full transition-all duration-500"
                  style={{ width: `${levelProgress}%` }}
                />
              </div>
              <p className="text-xs text-courtyard-stone/80 mt-1">
                {levelProgress}% to next level
              </p>
            </Card>
          </div>
        </motion.section>

        {/* Quick Actions */}
        <motion.section
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={4}
        >
          <h2 className="font-display text-2xl font-bold text-courtyard-stone mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {QUICK_ACTIONS.map(({ label, to, icon: Icon, colour, bg, border }) => (
              <Link key={to} to={to}>
                <Card className={`p-4 text-center hover:shadow-courtyard-md transition-all hover:-translate-y-0.5 ${bg} ${border}`}>
                  <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${bg}`}>
                    <Icon className={`w-5 h-5 ${colour}`} aria-hidden="true" />
                  </div>
                  <span className="text-sm font-medium text-courtyard-stone">
                    {label}
                  </span>
                </Card>
              </Link>
            ))}
          </div>
        </motion.section>

        {/* From the Courtyard — tip of the day */}
        {dailyTip && (
          <motion.section
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={5}
          >
            <Card className={`p-6 ${
              dailyTip.characterId === 'philemon' ? 'bg-philemon-bg/40 border-courtyard-olive/20' :
              dailyTip.characterId === 'xenia' ? 'bg-xenia-bg/40 border-courtyard-aegean/20' :
              'bg-theron-bg/40 border-courtyard-terracotta/20'
            }`}>
              <div className="flex items-start gap-4">
                <CharacterAvatar characterId={dailyTip.characterId} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wide text-courtyard-stone/80 font-medium mb-1">
                    From the Courtyard
                  </p>
                  <p className="text-courtyard-stone/80 italic leading-relaxed">
                    &ldquo;{dailyTip.text}&rdquo;
                  </p>
                  <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                    <p className={`text-sm font-medium ${
                      dailyTip.characterId === 'philemon' ? 'text-courtyard-olive-dark' :
                      dailyTip.characterId === 'xenia' ? 'text-courtyard-aegean-dark' :
                      'text-courtyard-clay'
                    }`}>
                      {dailyTip.characterName}
                    </p>
                    {dailyTip.link && (
                      <Link
                        to={dailyTip.link.to}
                        className="inline-flex items-center gap-1 text-sm text-courtyard-gold-ink hover:text-courtyard-clay font-medium transition-colors"
                      >
                        {dailyTip.link.label} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </motion.section>
        )}
      </div>
    </>
  );
};

export default HomePage;
